import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IEnergyEmission } from '../../../../../../../../../../../../hooks/api/energy-emissions';
import useDialogContext from '../../../../../../../../../../../../hooks/dialog/useDialogContext';
import { EmissionValueWithSuffix } from '../../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { CountryCodeToLabelMap } from '../../../../../../../../../../../../shared/helpers/country-region-helpers';
import { DeclaredUnit } from '../../../../../../../../../../../../shared/interfaces/IComponentDetails';
import { EmissionSymbol } from '../../../../../../../../../../../../shared/interfaces/IFootprint';
import styles from './Menu.module.css';

type MenuProps = {
  filteredEnergyEmissions: Partial<IEnergyEmission>[];
  onItemSelect: (item: Partial<IEnergyEmission>) => void;
  onItemMouseDown: (e: React.MouseEvent<HTMLElement>) => void;
};

// Pre-compute label and element once per emission
const getItemLabel = (emission: Partial<IEnergyEmission>) => {
  if (emission?.year && emission?.countryCode) {
    const country = CountryCodeToLabelMap[emission.countryCode as string];

    return {
      label: `${country} ${emission.year}`,
      element: (
        <div className='flex flex-row gap-1 flex-wrap'>
          <span className='white-space-normal'>{country}</span>|<span>{emission.year}</span>
        </div>
      ),
    };
  }

  return {
    label: '',
    element: (
      <div className='flex flex-row flex-wrap'>
        <span>{emission.name}</span>
      </div>
    ),
  };
};

const Menu = ({
  filteredEnergyEmissions,
  onItemMouseDown,
  onItemSelect,
}: MenuProps): JSX.Element => {
  const { t } = useTranslation();

  const { openDialog } = useDialogContext();

  const sortedItems = useMemo(() => {
    return [...filteredEnergyEmissions]
      .map((emission) => {
        return {
          emission,
          ...getItemLabel(emission),
        };
      })
      .sort((a, b) => {
        if (a.label && !b.label) return -1;
        if (!a.label && b.label) return 1;

        return a.label.localeCompare(b.label);
      });
  }, [filteredEnergyEmissions]);

  return (
    <div className='relative' onMouseDown={onItemMouseDown}>
      <div className='p-dropdown-panel p-component p-ripple-disabled p-connected-overlay-enter-done z-2 absolute w-full overflow-auto'>
        <div className='p-dropdown-items-wrapper max-h-15rem'>
          <ul className='p-dropdown-items'>
            {filteredEnergyEmissions.length > 0 ? (
              <>
                {sortedItems.map(({ emission, element }, index) => {
                  return (
                    <li
                      key={emission.id || index}
                      className='p-dropdown-item'
                      onClick={(): void => {
                        return onItemSelect(emission);
                      }}
                    >
                      <div className='flex justify-content-between w-full'>
                        <div className='flex w-9'>{element}</div>

                        <EmissionValueWithSuffix
                          emissionValue={emission.emissionFactor}
                          declaredUnit={DeclaredUnit.kilowattHour}
                          unitaryProductAmount={1}
                          emissionSymbol={EmissionSymbol.G_CO2E}
                          abbreviateUnit={true}
                          emissionValueDecimals={6}
                          suffixTextClassName='font-normal'
                          numericalValueTextClassName='font-normal'
                        />
                      </div>
                    </li>
                  );
                })}
              </>
            ) : (
              <li className='p-dropdown-item cursor-auto'>
                <div className='flex w-full'>
                  <span>
                    {t('calculationWizard.sections.generalInformationSection.noEmissionsFound')}
                  </span>
                </div>
              </li>
            )}
          </ul>
        </div>
        <div className='p-dropdown-footer'>
          <div
            className={styles['enter-manually-option']}
            onClick={(): void => {
              openDialog('input-electricity-emissions-info-dialog');
            }}
          >
            {t('global.enterManually')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Menu;
